import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { find } from 'lodash';

import { ButtonModel, Text } from '@notino/react-styleguide';

import { useAnimateUnmount } from '@components/Animate/useAnimateUnmount';
import { useGetImageAttributes } from '@components/CdnImage/useImageAttributes';
import { useEngravingTryItFirstContext } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/context/EngravingTryItFirst';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { ProductEventWither } from '@helpers/googleTagManager';

import { useShadefinderTransitions } from '../../../hooks/useShadefinderTransitions';
import { useTrackSelect } from '../../../hooks/useTrackSelect';
import { useTrackShadefinderLoaded } from '../../../hooks/useTrackShadefinderDisplayResult';
import { messages } from '../../../messages';
import { trackShadefinderSelectResult } from '../../../tracking';
import { RecommendedProduct } from '../../../types';
import ShareButtonComponent from '../../shareButton/ShareButton';
import { StepContainer } from '../../StepContainer';
import { ItemLink } from '../../styled';

import {
  ButtonWrapper,
  Grid,
  Header,
  SelectedShade,
  StyledChooseShadeButton,
  StyledItem,
  StyledItemLink,
} from './styled';
import { TryItFirst } from './TryItFirst';
import { useShowTryItFirst } from './TryItFirst/useShowTryItFirst';

interface IShadefinderStep4Props {
  recommendedVariant: RecommendedProduct;
  timeOfStart: number;
}
export const ShadefinderStep4: React.FC<IShadefinderStep4Props> = ({
  recommendedVariant,
  timeOfStart,
}) => {
  const { close } = useShadefinderTransitions();
  const { formatMessage } = useIntl();
  const { product } = useProductDetailContext();
  const [unmounting, animateTransition] = useAnimateUnmount();
  const getImageAttributes = useGetImageAttributes();
  const trackSelect = useTrackSelect(timeOfStart);

  const {
    actions: { toggleTryItFirst },
  } = useEngravingTryItFirstContext();
  const [tifChecked, toggleTifChecked] = React.useReducer(
    (prev) => !prev,
    false
  );

  const recommendedVariantObj = find(product.variants, {
    webId: String(recommendedVariant.productId),
  })!;
  const recommendedVariantUrl = recommendedVariantObj?.url;

  useTrackShadefinderLoaded(recommendedVariantObj, timeOfStart);

  const showTryItFirst = useShowTryItFirst(recommendedVariantObj?.webId ?? '');

  const handleLinkClicked = () => {
    if (showTryItFirst) {
      toggleTryItFirst(tifChecked);
    }
    close();
  };

  const linkProps = {
    to: recommendedVariantUrl ?? '',
    replace: true,
    onClick: animateTransition(handleLinkClicked, {
      beforeTransition: () =>
        trackSelect(({ timing, variant }) =>
          trackShadefinderSelectResult({
            timing,
            variant,
            product: ProductEventWither()
              .withProduct(product)
              .withVariant(recommendedVariantObj)
              .build(),
          })
        ),
    }),
  };

  return (
    <StepContainer unmounting={unmounting} containerJustifyCenter={true}>
      <Grid>
        <Header>
          <FormattedMessage {...messages.recommendedVariantTitle} />
          <br />
          <SelectedShade>{recommendedVariant.variantName}</SelectedShade>
          <br />

          {!showTryItFirst && (
            <Text
              typography="bodyRegular"
              color="text.secondary"
              marginTop="0.75rem"
            >
              <FormattedMessage {...messages.recommendedVariantDescription} />
            </Text>
          )}
        </Header>

        <StyledItemLink {...linkProps}>
          <StyledItem
            imageUrl={
              getImageAttributes(recommendedVariant.mainImage, 'zoomImage').src
            }
          />
        </StyledItemLink>

        <ButtonWrapper>
          {showTryItFirst && (
            <TryItFirst isChecked={tifChecked} onClick={toggleTifChecked} />
          )}

          <ItemLink {...linkProps}>
            <StyledChooseShadeButton buttonStyle={ButtonModel.Styles.primary}>
              {formatMessage(messages.selectVariantButton)}
            </StyledChooseShadeButton>
          </ItemLink>
          <ShareButtonComponent direction="down" />
        </ButtonWrapper>
      </Grid>
    </StepContainer>
  );
};
